import type { HsHlodTilesetLink } from '@sibe/hyperscene';
import { HS_HLOD_REGION_ID } from './hlod-tileset.class';

/** The endpoints the lookup reads; `HsViewerApi` provides them. */
export interface HsHlodLookupSource {
  hasPhotogrammetry(sceneId: string): Promise<boolean>;
  getPhotogrammetryTilesets(meshIds: readonly string[]): Promise<HsHlodTilesetLink[]>;
}

export interface HsHlodTilesetLookupOptions {
  /** Mesh ids per request (default 1024). */
  batchSize?: number;
  /** Clock for the retry backoff, in milliseconds. */
  now?: () => number;
}

/**
 * Finds the HLOD tilesets of drawn meshes. Each mesh is asked about once per scene; scenes the API
 * reports as having no photogrammetry skip the lookups, and a failed request backs off (0.5 to 16 s) so
 * a rejected batch is not sent again at every camera stop.
 */
export class HsHlodTilesetLookup {
  private readonly lookedUp: Set<string> = new Set<string>();
  private photogrammetryCheck: { sceneId: string; result: Promise<boolean> } | null = null;
  private failures: number = 0;
  private retryAt: number = 0;
  private readonly batchSize: number;
  private readonly now: () => number;

  public constructor(private readonly source: HsHlodLookupSource, options: HsHlodTilesetLookupOptions = {}) {
    this.batchSize = options.batchSize ?? 1024;
    this.now = options.now ?? ((): number => performance.now());
  }

  /** True while the lookup waits out the backoff after a failure. */
  public get coolingDown(): boolean {
    return this.now() < this.retryAt;
  }

  /**
   * Asks for tilesets of the meshes not asked about yet and calls `onTileset` for each HLOD link.
   * It stops without recording the batch when `isCurrent()` turns false (the scene changed).
   */
  public async lookup(
    sceneId: string | null,
    meshIds: readonly string[],
    isCurrent: () => boolean,
    onTileset: (link: HsHlodTilesetLink) => void
  ): Promise<void> {
    const unknown: string[] = meshIds.filter((id: string) => !this.lookedUp.has(id));
    if (unknown.length === 0 || !(await this.mayHavePhotogrammetry(sceneId))) {
      return;
    }
    try {
      for (let start: number = 0; start < unknown.length; start += this.batchSize) {
        const batch: string[] = unknown.slice(start, start + this.batchSize);
        const links: HsHlodTilesetLink[] = await this.source.getPhotogrammetryTilesets(batch);
        if (!isCurrent()) {
          return;
        }
        batch.forEach((id: string) => this.lookedUp.add(id));
        links.filter((link: HsHlodTilesetLink) => link.regionId === HS_HLOD_REGION_ID).forEach(onTileset);
      }
      this.failures = 0;
    } catch (error: unknown) {
      this.failures++;
      this.retryAt = this.now() + Math.min(64, 2 ** this.failures) * 250;
      console.warn('HLOD tileset lookup failed.', error);
    }
  }

  /** Forgets the meshes asked about, the scene check and the backoff, for a new scene. */
  public reset(): void {
    this.lookedUp.clear();
    this.photogrammetryCheck = null;
    this.failures = 0;
    this.retryAt = 0;
  }

  /** One request per scene; without a scene, or when the check fails, the lookups stay enabled. */
  private mayHavePhotogrammetry(sceneId: string | null): Promise<boolean> {
    if (sceneId === null) {
      return Promise.resolve(true);
    }
    if (this.photogrammetryCheck?.sceneId !== sceneId) {
      this.photogrammetryCheck = {
        sceneId,
        result: this.source.hasPhotogrammetry(sceneId).catch((): boolean => true),
      };
    }
    return this.photogrammetryCheck.result;
  }
}
